import React, { useEffect, useState } from 'react';
import { Megaphone, Trophy, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { easerPost } from '../../lib/easerApi';
import { notificationImageUrl, KIND_LABELS, markPopupShown, dismissBanner, notifyNotificationsChanged } from '../../lib/easerNotifications';

/**
 * Pop-ups shown at the start of the app, one notification after the other. Closing one records its first read
 * (once, server side) and marks it shown for this session.
 */
export function NotificationPopup({ queue, onUnauthorized, onRead }) {
  const [index, setIndex] = useState(0);
  const current = queue[index] || null;

  useEffect(() => { setIndex(0); }, [queue]);

  const close = async () => {
    if (!current) return;
    markPopupShown(current.id);
    if (!current.read_at) {
      try {
        const res = await easerPost(`/api/easer/notifications/${current.id}/read`);
        onRead?.(current.id, res.data.read_at);
      } catch (err) {
        if (err?.response?.status === 401) onUnauthorized?.();
      }
    }
    notifyNotificationsChanged();
    setIndex((i) => i + 1);
  };

  if (!current) return null;
  const challenge = current.kind === 'challenge';
  return (
    <Dialog open onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-md w-[95vw] rounded-2xl max-h-[92vh] overflow-y-auto" data-testid="notification-popup">
        <DialogHeader className="text-left">
          <div className="flex items-center gap-2">
            <span className={`h-9 w-9 rounded-xl flex items-center justify-center ${challenge ? 'bg-[hsl(var(--brand-apricot))]/15 text-[hsl(var(--brand-apricot))]' : 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]'}`}>
              {challenge ? <Trophy className="h-5 w-5" /> : <Megaphone className="h-5 w-5" />}
            </span>
            <Badge variant="outline" className="text-[10px] uppercase tracking-wide">{KIND_LABELS[current.kind] || 'Message'}</Badge>
            {queue.length > 1 && <span className="text-xs text-muted-foreground ml-auto" data-testid="notification-popup-counter">{index + 1} / {queue.length}</span>}
          </div>
          <DialogTitle className="font-['Space_Grotesk'] text-xl leading-snug" data-testid="notification-popup-title">{current.title}</DialogTitle>
          <DialogDescription className="sr-only">Notification de l'agence</DialogDescription>
        </DialogHeader>
        {current.has_image && <img src={notificationImageUrl(current.id)} alt="" className="w-full rounded-xl border border-border max-h-72 object-contain bg-secondary/40" />}
        {current.body && <p className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="notification-popup-body">{current.body}</p>}
        <DialogFooter>
          <Button onClick={close} className="w-full h-11 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="notification-popup-close">
            {queue.length > index + 1 ? 'Suivant' : "J'ai lu, fermer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Thin banners at the top of the portal (display_mode banner, or both once read), closable for the session. */
export function NotificationBanners({ items, onOpen, onDismiss }) {
  if (!items.length) return null;
  return (
    <div className="space-y-2 mb-4" data-testid="notification-banners">
      {items.slice(0, 3).map((n) => (
        <div key={n.id} className={`rounded-xl border px-3 py-2 text-sm flex items-center gap-2 ${n.kind === 'challenge' ? 'border-[hsl(var(--brand-apricot))]/50 bg-[hsl(var(--brand-apricot))]/10' : 'border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5'}`} role="status" data-testid="notification-banner">
          {n.kind === 'challenge' ? <Trophy className="h-4 w-4 text-[hsl(var(--brand-apricot))] flex-shrink-0" /> : <Megaphone className="h-4 w-4 text-[hsl(var(--primary))] flex-shrink-0" />}
          <button type="button" onClick={() => onOpen(n)} className="min-w-0 flex-1 text-left" data-testid="notification-banner-open">
            <span className="font-semibold block truncate">{n.title}</span>
            {n.body && <span className="text-muted-foreground block truncate text-xs">{n.body}</span>}
          </button>
          <button type="button" onClick={() => { dismissBanner(n.id); onDismiss(n.id); }} aria-label="Fermer" className="p-2 -mr-1 text-muted-foreground hover:text-foreground" data-testid="notification-banner-close"><X className="h-4 w-4" /></button>
        </div>
      ))}
    </div>
  );
}
